import type { Word, Pairing, GridText } from '../types'

/**
 * Canonical builder. The only way to mint a `GridText`.
 */
export function toGridText(text: string): GridText {
  return text.replace(/\s/g, '').toUpperCase() as GridText
}

/**
 * Get the letters of a word, stripping spaces.
 * Use this for all crossword logic (length matching, letter positions).
 * The original word.text is preserved for display.
 */
export function getLetters(word: Word): GridText {
  return toGridText(word.text)
}

/**
 * Get letters from a raw string (for when you don't have a Word object)
 */
export function getLettersFromText(text: string): GridText {
  return toGridText(text)
}

/**
 * Get letters as an uppercase array from a raw string.
 * Use this when you need to iterate over individual letters.
 */
export function getLetterArray(text: string): string[] {
  return toGridText(text).split('')
}

/**
 * Get all possible pairings for a word (same-length words from the opposite list)
 * Words without a list assignment cannot be paired.
 */
export function getPossiblePairings(
  word: Word,
  allWords: Word[],
  existingPairings: Pairing[]
): { word: Word; pairing: Pairing | null }[] {
  // Unassigned words can't be paired
  if (!word.list) return []
  
  const targetLength = getLetters(word).length
  const oppositeList = word.list === 'A' ? 'B' : 'A'
  
  // Find words from the opposite list with matching length (ignoring spaces)
  const matchingWords = allWords.filter(
    w => w.list === oppositeList && getLetters(w).length === targetLength
  )
  
  // Map to include existing pairing if any
  return matchingWords.map(matchingWord => {
    const existingPairing = existingPairings.find(p => {
      if (word.list === 'A') {
        return p.wordAId === word.id && p.wordBId === matchingWord.id
      } else {
        return p.wordAId === matchingWord.id && p.wordBId === word.id
      }
    })
    
    return {
      word: matchingWord,
      pairing: existingPairing || null
    }
  })
}

/**
 * Get all words in the same group as the given word
 */
export function getWordsInGroup(word: Word, allWords: Word[]): Word[] {
  if (!word.groupId) return [word]
  return allWords.filter(w => w.groupId === word.groupId)
}

/**
 * Get all twin word IDs across lists — used for grid-suggestion dedup so that
 * placing PROCREATE in list A also excludes PROCREATE (and its variants) in list B.
 */
export function getTwinWordIds(word: Word, allWords: Word[]): string[] {
  if (!word.twinGroupId) return []
  return allWords.filter(w => w.twinGroupId === word.twinGroupId).map(w => w.id)
}

/**
 * Splits any group whose members span multiple lists into per-list groups.
 * Singletons left after splitting lose their groupId. Returns the same array
 * reference if no migration was needed.
 */
export function splitCrossListGroups(words: Word[]): Word[] {
  const membersByGroup = new Map<string, Word[]>()
  for (const word of words) {
    if (!word.groupId) continue
    const list = membersByGroup.get(word.groupId)
    if (list) list.push(word)
    else membersByGroup.set(word.groupId, [word])
  }

  const replacementsByGroup = new Map<string, Map<string, string | null>>()
  for (const [groupId, members] of membersByGroup) {
    const byList = new Map<string, Word[]>()
    for (const m of members) {
      const k = m.list ?? 'unsorted'
      const arr = byList.get(k)
      if (arr) arr.push(m)
      else byList.set(k, [m])
    }
    if (byList.size <= 1) continue

    const replacement = new Map<string, string | null>()
    for (const [k, listMembers] of byList) {
      replacement.set(k, listMembers.length >= 2 ? generateId() : null)
    }
    replacementsByGroup.set(groupId, replacement)
  }

  if (replacementsByGroup.size === 0) return words

  return words.map(word => {
    if (!word.groupId) return word
    const replacement = replacementsByGroup.get(word.groupId)
    if (!replacement) return word
    const newGroupId = replacement.get(word.list ?? 'unsorted')
    if (newGroupId === undefined || newGroupId === null) {
      const next: Word = { ...word }
      delete next.groupId
      return next
    }
    return { ...word, groupId: newGroupId }
  })
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return crypto.randomUUID()
}

/**
 * Get the paired word for a pairing
 */
export function getPairedWord(
  pairing: Pairing,
  fromWord: Word,
  allWords: Word[]
): Word | undefined {
  const targetId = fromWord.list === 'A' ? pairing.wordBId : pairing.wordAId
  return allWords.find(w => w.id === targetId)
}

/**
 * Create a new pairing between two words.
 * Returns null if words are not from different lists (one from A, one from B).
 */
export function createPairing(wordA: Word, wordB: Word): Pairing | null {
  // Validate that we have one word from each list
  const lists = new Set([wordA.list, wordB.list])
  if (!lists.has('A') || !lists.has('B') || lists.size !== 2) {
    return null
  }

  // Ensure wordA is from list A and wordB is from list B
  const [actualA, actualB] = wordA.list === 'A' ? [wordA, wordB] : [wordB, wordA]

  // New pairings default to 'draft' — the "yeah, look at this later" bucket.
  // 'none' is reserved for unrecorded combinations that exist only as a
  // possibility (no Pairing record); creating a record always implies at
  // least a draft-level commit.
  return {
    id: generateId(),
    wordAId: actualA.id,
    wordBId: actualB.id,
    status: 'draft'
  }
}

/**
 * Get both words for a pairing
 */
export function getPairingWords(
  pairing: Pairing,
  allWords: Word[]
): { wordA: Word | undefined; wordB: Word | undefined } {
  return {
    wordA: allWords.find(w => w.id === pairing.wordAId),
    wordB: allWords.find(w => w.id === pairing.wordBId)
  }
}

/**
 * Look up a pairing record by its word IDs. Returns null when no metadata
 * exists for this combination — the pair is still conceptually valid; it just
 * doesn't have a recorded status / clue yet. Order-independent.
 */
export function getPairingByWords(
  pairings: Pairing[],
  wordAId: string,
  wordBId: string
): Pairing | null {
  for (const p of pairings) {
    if (
      (p.wordAId === wordAId && p.wordBId === wordBId) ||
      (p.wordAId === wordBId && p.wordBId === wordAId)
    ) {
      return p
    }
  }
  return null
}

/**
 * Whether a pair is placed in any of the supplied placement-id sets. Status
 * is stored separately (curatorial axis) — used / unused is a derived axis
 * the consumer composes with whatever scope makes sense (active draft only,
 * any draft, scratchpad, etc).
 */
export function isPairingUsed(
  pairing: Pairing,
  placedPairingIds: ReadonlySet<string>,
): boolean {
  return placedPairingIds.has(pairing.id)
}

/**
 * Tristate used/unused filter. 'all' is the no-op default. Pairs without a
 * pairing record are treated as unused (no metadata, never placed).
 */
export function passesUsedFilter(
  pairingId: string | null | undefined,
  filter: 'all' | 'used' | 'unused',
  placedPairingIds: ReadonlySet<string>,
): boolean {
  if (filter === 'all') return true
  const placed = pairingId ? placedPairingIds.has(pairingId) : false
  return filter === 'used' ? placed : !placed
}
